import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import cloneDeep from 'lodash-es/cloneDeep'
import moment from 'moment'
import CheckBox from '../common/CheckBox'
import FontNumber from '../common/FontNumber'
import ScrollableTable from './ScrollableTable'
import OrderHistoryFilter from './OrderHistoryFilter'
import TxHash from '../order/TxHash'
import { CellItem, FilledItem, CellItemRight } from './OpenOrders'
import Side from '../order/Side'
import Status from '../order/Status'
import { pencentage, roundValue } from '../../utils/number'
import reduceTradePairBSufix from '../../utils/tradepair'
import convertOrderStatus from '../../utils/convertOrderStatus'
import { withI18n } from '../../i18n/util'

const Header = styled.div`
  height: 30px;
  padding: 0 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.exchangeFilterBg};
`

const HideOtherPairs = styled.div`
  position: relative;
`

const Content = styled.div`
  margin-top: 10px;
  height: 200px;
  position: relative;
  padding: 0 6px;
  .table-wrap {
    .fixed-table {
      bottom: 20px;
    }
  }
  table tbody tr {
    padding: 0;
    height: 25px;
    line-height: 25px;
    &:nth-child(odd) {
      background: ${({ theme }) => theme.tableOddRowBg};
    }
  }
`

const CellItemCancel = styled(CellItem)`
  color: #48515d;
`

const CancelItemRight = styled(CellItemCancel)`
  text-align: right;
`

class OrderHistory extends PureComponent {
  static propTypes = {
    orderHistory: PropTypes.array,
    currentTrade: PropTypes.string,
    getOrderHistory: PropTypes.func,
    formatI18nText: PropTypes.func,
  }

  static defaultProps = {
    orderHistory: [],
    currentTrade: '',
    getOrderHistory: () => {},
    formatI18nText: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      hidePairs: false,
      start: null,
      end: null,
    }
  }

  _handleCheckChange = e => {
    const { getOrderHistory, currentTrade } = this.props
    const { start, end } = this.state
    const checked = e.target.checked //eslint-disable-line
    this.setState({ hidePairs: checked }, () => {
      const opts = {}
      if (checked) {
        opts.symbol = currentTrade
      }
      if (start) {
        opts.start = start
      }
      if (end) {
        opts.end = end
      }
      getOrderHistory && getOrderHistory(opts)
    })
  }

  _buildColumns = () => {
    const { formatI18nText } = this.props
    const columns = [
      {
        title: formatI18nText('exchange.txHash'),
        headerAlign: 'left',
        dataIndex: 'txHash',
        key: 'TxHash',
        width: '20%',
        render: item => <TxHash hash={item.txHash} />,
      },
      {
        title: formatI18nText('exchange.date'),
        dataIndex: 'date',
        headerAlign: 'left',
        key: 'Date',
        width: '12%',
        render: item => (
          <CellItem>
            <FontNumber value={moment(item.date).format('MM-DD HH:mm:ss')} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.pair'),
        dataIndex: 'pair',
        headerAlign: 'left',
        key: 'Pair',
        width: '13%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return <CellItemCancel>{item.pair}</CellItemCancel>
          }
          return <CellItem>{item.pair}</CellItem>
        },
      },
      {
        title: formatI18nText('exchange.side'),
        dataIndex: 'side',
        headerAlign: 'left',
        key: 'Side',
        width: '5%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return <CellItemCancel>{item.side}</CellItemCancel>
          }
          return <Side text={item.side} />
        },
      },
      {
        title: formatI18nText('exchange.price'),
        dataIndex: 'price',
        headerAlign: 'left',
        key: 'Price',
        width: '10%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return (
              <CellItemCancel>
                <FontNumber value={item.price} />
              </CellItemCancel>
            )
          }
          return (
            <CellItem>
              <FontNumber value={item.price} />
            </CellItem>
          )
        },
      },
      {
        title: formatI18nText('exchange.amount'),
        dataIndex: 'amount',
        headerAlign: 'left',
        key: 'Amount',
        width: '10%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return (
              <CellItemCancel>
                <FontNumber value={item.amount} />
              </CellItemCancel>
            )
          }
          return (
            <CellItem>
              <FontNumber value={item.amount} />
            </CellItem>
          )
        },
      },
      {
        title: formatI18nText('exchange.filled'),
        dataIndex: 'filled',
        headerAlign: 'right',
        key: 'Filled',
        width: '5%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return (
              <CancelItemRight>
                <FontNumber value={item.filled} />
              </CancelItemRight>
            )
          }
          return (
            <FilledItem fill={true}>
              <FontNumber value={item.filled} />
            </FilledItem>
          )
        },
      },
      {
        title: formatI18nText('exchange.notional'),
        dataIndex: 'total',
        headerAlign: 'right',
        key: 'Total',
        width: '15%',
        render: item => {
          if (item.status.toUpperCase() === 'CANCELED') {
            return (
              <CancelItemRight>
                <FontNumber value={item.total} />
              </CancelItemRight>
            )
          }
          return (
            <CellItemRight>
              <FontNumber value={item.total} />
            </CellItemRight>
          )
        },
      },
      {
        title: formatI18nText('exchange.status'),
        dataIndex: 'status',
        headerAlign: 'right',
        key: 'Status',
        width: '10%',
        render: item => (
          <div style={{ textAlign: 'right', paddingRight: '3px' }}>
            <Status text={item.status} />
          </div>
        ),
      },
    ]
    return columns
  }

  _buildDataSource = () => {
    const { orderHistory } = this.props
    const result = []
    orderHistory.forEach(order => {
      result.push({
        txHash: order.transactionHash,
        date: order.orderCreateTime,
        pair: reduceTradePairBSufix(order.symbol),
        side: order.side === 1 ? 'Buy' : 'Sell',
        average: order.average,
        price: roundValue(order.price),
        amount: roundValue(order.quantity),
        cumulateQuantity: roundValue(order.cumulateQuantity),
        filled: pencentage(order.cumulateQuantity, order.quantity),
        total: roundValue(order.price * order.quantity),
        status: convertOrderStatus(order.status),
      })
    })
    return result
  }

  _onFilterChange = opts => {
    const { getOrderHistory } = this.props
    const { start, end } = opts
    this.setState({ start, end }, () => {
      getOrderHistory && getOrderHistory(opts)
    })
  }

  render() {
    const columns = this._buildColumns()
    const dataSource = this._buildDataSource()
    const { hidePairs } = this.state
    const { formatI18nText } = this.props

    return (
      <div>
        <Header>
          <HideOtherPairs>
            <CheckBox
              labelTitle={formatI18nText('exchange.hideOtherPairs')}
              defaultChecked={hidePairs}
              handleCheckChange={this._handleCheckChange}
            />
          </HideOtherPairs>
          <OrderHistoryFilter onFilterChange={this._onFilterChange} />
        </Header>
        <Content>
          <ScrollableTable columns={columns} dataSource={dataSource} isFixedHeader={true} />
          {
            // dataSource.length === 0 && <LoadingShade />
          }
        </Content>
      </div>
    )
  }
}

const mapStateToProps = ({ exchange: { currentTrade }, order: { orderHistory } }) => ({
  orderHistory: cloneDeep(orderHistory.order),
  currentTrade,
})

export default withI18n(connect(mapStateToProps)(OrderHistory))
